import { useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'

/**
 * A hook to typecheck and null check useParams.
 * We do this because react-router can never be sure of where we are calling useParams from.
 * Even if we are sure IN THIS MOMENT, in the future, a route component could be deleted or a param name could change
 * This gives us a test for that case
 *
 * Code taken from {@link https://github.com/remix-run/react-router/issues/8200#issuecomment-1193097483}
 *
 * Example usage: `const { organizerSlug } = useTypedParams(['organizerSlug']);`
 *
 * @param parameterNames - the keys of the dynamic route params from the current url
 *
 * @throws {Error} If any route param is falsy
 */
export function useTypedParams<T extends string>(parameterNames: T[]): Record<T, string> {
    const params = useParams()
    const typedParams: Record<string, string> = {}
    parameterNames.forEach((paramName) => {
        const currentParam = params[paramName]
        invariant(currentParam, `${paramName} not found in useParams. Check the parent route to make sure nothing changed`)
        typedParams[paramName] = currentParam
    })
    return typedParams
}

export function useOptionalTypedParams<T extends string>(parameterNames: T[]): Record<T, string | undefined> {
    const params = useParams()
    const typedParams: Record<string, string | undefined> = {}
    parameterNames.forEach((paramName) => {
        typedParams[paramName] = params[paramName]
    })
    return typedParams
}

export default {
    useTypedParams,
    useOptionalTypedParams,
}
